import { useState, useMemo, useCallback } from 'react';
import {
  Box,
  CircularProgress,
  Alert,
  Typography,
  Chip,
  ListItem,
} from '@mui/material';
import { usePlatformOrganisationFeatures } from '../../hooks/usePlatformOrganisationFeatures';
import { OrgFeatureSwitchDto } from '../../api/platformApi';
import {
  useSetOrgOverride,
  useRemoveOrgOverride,
} from '../../hooks/useFeatureOverrideMutations';
import FeatureToggleConfirmDialog, {
  FeatureToggleAction,
  OverrideState,
} from '../FeatureToggleConfirmDialog';
import { usePermission, Permissions } from '@billda/permissions-ui';
import { PageToolbar } from '../../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../../shared/components/ui/ChipTabs';
import { ListViewShell } from '../../../../shared/components/ui/ListViewShell';

type SourceFilter = 'all' | 'org' | 'global' | 'default';

const SOURCE_TABS = [
  { label: 'All', value: 'all' },
  { label: 'Org Override', value: 'org' },
  { label: 'Global', value: 'global' },
  { label: 'Default', value: 'default' },
] as const;

interface OrgFeaturesTabProps {
  orgId: string;
}

function getEffectiveChip(feature: OrgFeatureSwitchDto) {
  if (feature.source === 'default') {
    return { label: 'Default (off)', color: 'default' as const };
  }
  if (feature.effectiveState === 'enabled') {
    return { label: 'Enabled', color: 'success' as const };
  }
  return { label: 'Disabled', color: 'error' as const };
}

function toOrgCurrentState(feature: OrgFeatureSwitchDto): OverrideState {
  if (feature.source === 'default') return 'default';
  return feature.effectiveState === 'enabled' ? 'enabled' : 'disabled';
}

function getOrgFallback(feature: OrgFeatureSwitchDto): {
  state: OverrideState;
  source: string;
} {
  // When removing an org override, feature falls back to global or default.
  // We don't know the global override state from the DTO, so describe the source.
  if (feature.source === 'org') {
    return { state: 'default', source: 'global override or default' };
  }
  return { state: 'default', source: 'default' };
}

function getOrgAction(
  feature: OrgFeatureSwitchDto,
  orgName: string
): FeatureToggleAction {
  const currentState = toOrgCurrentState(feature);
  if (feature.source === 'org') {
    const fallback = getOrgFallback(feature);
    return {
      type: 'remove',
      scope: 'org',
      orgName,
      currentState,
      fallbackState: fallback.state,
      fallbackSource: fallback.source,
    };
  }
  return { type: 'set', scope: 'org', orgName, currentState };
}

export const OrgFeaturesTab = ({ orgId }: OrgFeaturesTabProps) => {
  const [searchValue, setSearchValue] = useState('');
  const [sourceFilter, setSourceFilter] = useState<SourceFilter>('all');
  const [selectedFeature, setSelectedFeature] =
    useState<OrgFeatureSwitchDto | null>(null);

  const { data, isLoading, error } = usePlatformOrganisationFeatures(orgId);

  const canManageFeatures = usePermission(Permissions.Features.Manage);

  const setOrgOverride = useSetOrgOverride();
  const removeOrgOverride = useRemoveOrgOverride();

  const isMutating = setOrgOverride.isPending || removeOrgOverride.isPending;

  const organisationName = data?.organisationName ?? '';
  const features = useMemo(() => data?.features ?? [], [data?.features]);

  const filteredFeatures = useMemo(() => {
    let items = features;
    if (sourceFilter !== 'all') {
      items = items.filter(f => f.source === sourceFilter);
    }
    if (searchValue) {
      const search = searchValue.toLowerCase();
      items = items.filter(
        f =>
          f.key.toLowerCase().includes(search) ||
          f.description.toLowerCase().includes(search)
      );
    }
    return items;
  }, [features, sourceFilter, searchValue]);

  const handleRowClick = useCallback(
    (feature: OrgFeatureSwitchDto) => {
      if (!canManageFeatures) {
        return;
      }
      setSelectedFeature(feature);
    },
    [canManageFeatures]
  );

  const handleConfirm = useCallback(
    (isEnabled: boolean) => {
      if (!selectedFeature || !orgId) return;

      const action = getOrgAction(selectedFeature, organisationName);
      const onSuccess = () => setSelectedFeature(null);

      if (action.type === 'remove') {
        removeOrgOverride.mutate(
          { key: selectedFeature.key, orgId },
          { onSuccess }
        );
      } else {
        setOrgOverride.mutate(
          { key: selectedFeature.key, orgId, isEnabled },
          { onSuccess }
        );
      }
    },
    [
      selectedFeature,
      orgId,
      organisationName,
      setOrgOverride,
      removeOrgOverride,
    ]
  );

  const handleCancel = useCallback(() => {
    setSelectedFeature(null);
  }, []);

  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 200,
          p: 3,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error.message}
      </Alert>
    );
  }

  return (
    <Box>
      <PageToolbar
        tabs={
          <ChipTabs
            tabs={[...SOURCE_TABS]}
            value={sourceFilter}
            onChange={v => setSourceFilter(v as SourceFilter)}
            data-testid="source-filter-tabs"
          />
        }
        searchValue={searchValue}
        onSearchChange={setSearchValue}
      />

      <ListViewShell
        itemCount={filteredFeatures.length}
        loading={isLoading}
        emptyMessage="No feature switches found"
        data-testid="org-features-list"
        columnHeaders={
          <>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ flex: 1 }}
            >
              Feature
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                width: 120,
                textAlign: 'right',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Effective State
            </Typography>
          </>
        }
      >
        {filteredFeatures.map(feature => (
          <ListItem
            key={feature.key}
            data-testid="org-feature-row"
            onClick={() => handleRowClick(feature)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 1.5,
              px: { xs: 2, sm: 3 },
              borderBottom: '1px solid',
              borderColor: 'divider',
              cursor: canManageFeatures ? 'pointer' : 'default',
              '&:hover': canManageFeatures
                ? { bgcolor: 'action.hover' }
                : undefined,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {feature.key}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  display: 'block',
                }}
              >
                {feature.description}
              </Typography>
              {feature.source === 'org' && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'info.main',
                    fontWeight: 600,
                    fontSize: '0.65rem',
                  }}
                >
                  Org override
                </Typography>
              )}
            </Box>

            <Box
              sx={{
                width: 120,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Chip
                label={getEffectiveChip(feature).label}
                color={getEffectiveChip(feature).color}
                size="small"
                sx={{ fontSize: '0.7rem', height: 22 }}
              />
            </Box>
          </ListItem>
        ))}
      </ListViewShell>

      {selectedFeature && (
        <FeatureToggleConfirmDialog
          open={!!selectedFeature}
          featureKey={selectedFeature.key}
          featureDescription={selectedFeature.description}
          action={getOrgAction(selectedFeature, organisationName)}
          isLoading={isMutating}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}
    </Box>
  );
};
